import { type AssetVizHoverEvent, AssetVizMode } from '@asset-viz/defines'
import { ForecastAssetVizWindow } from 'features/assetVizAdapter'
import type {
  ShortTermCorrosionFeatureRectangle,
  ShortTermRateFeatureBounds,
} from 'features/dataLayers/generation/shortTermCorrosionRate/types'
import { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import type {
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoInspection,
  GeckoMeasurementRange,
} from 'types'

import {
  buildFeatureRectangleMarkupLayer,
  getCombinedBounds,
  getPaddedFeatureZoomRect,
  type ShortTermCorrosionWorkflowMarkupReferenceData,
} from './featureMarkup'

const PreviewUnavailable = styled.div`
  display: flex;
  height: 100%;
  min-height: 28rem;
  align-items: center;
  justify-content: center;
  background-color: #000;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: rgb(255 255 255 / 0.6);
`

const VizContainer = styled.div`
  position: relative;
  height: 100%;
  min-height: 28rem;
  background-color: #000;
`

const TitleOverlay = styled.div`
  min-width: 0;
  max-width: 16rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 0.25rem;
  background-color: rgb(0 0 0 / 0.7);
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
  color: #fff;
  box-shadow:
    0 1px 3px 0 rgb(0 0 0 / 0.1),
    0 1px 2px -1px rgb(0 0 0 / 0.1);
  backdrop-filter: blur(8px);
`

const StyledAssetVizWindow = styled(ForecastAssetVizWindow)`
  width: 100%;
  height: 100%;
  min-height: 0;
`

export type ShortTermCorrosionWorkflowVisualizationReferenceData =
  ShortTermCorrosionWorkflowMarkupReferenceData & {
    assetModel?: GeckoAssetModel
    components: readonly GeckoComponent[]
    unit?: unknown
  }

export function WorkflowAssetVizPanel({
  title,
  referenceData,
  componentId,
  inspection,
  cdf,
  resolutionCdfs,
  onSelectResolution,
  featureRectangles,
  selectedFeatureId,
  zoomBoundsFeet,
  onHoveredCoordsChange,
  selectedColorThreshold,
  onSelectedColorThresholdChange,
  filteredValueRange,
  onFilteredValueRangeChange,
  showColorScaleSettings,
}: {
  title: string
  referenceData: ShortTermCorrosionWorkflowVisualizationReferenceData
  componentId?: string
  inspection?: GeckoInspection
  cdf?: GeckoComponentDataFrame
  resolutionCdfs?: readonly GeckoComponentDataFrame[]
  onSelectResolution?: (cdf: GeckoComponentDataFrame) => void
  featureRectangles: readonly ShortTermCorrosionFeatureRectangle[]
  selectedFeatureId?: string
  zoomBoundsFeet?: ShortTermRateFeatureBounds
  onHoveredCoordsChange: (coords: GeckoComponentCoords | undefined) => void
  selectedColorThreshold?: GeckoColorScaleThreshold
  onSelectedColorThresholdChange?: (
    threshold: GeckoColorScaleThreshold | undefined
  ) => void
  filteredValueRange?: GeckoMeasurementRange
  onFilteredValueRangeChange?: (
    range: GeckoMeasurementRange | undefined
  ) => void
  showColorScaleSettings?: boolean
}) {
  const component = useMemo(
    () =>
      referenceData.components.find(
        (candidate) => candidate.id === componentId
      ),
    [componentId, referenceData.components]
  )
  const markupLayers = useMemo(() => {
    if (!component || !referenceData.site || !referenceData.unit) return []
    return featureRectangles.map((featureRectangle) =>
      buildFeatureRectangleMarkupLayer({
        featureRectangle,
        componentId: component.id,
        referenceData,
        isSelected: featureRectangle.id === selectedFeatureId,
      })
    )
  }, [component, featureRectangles, referenceData, selectedFeatureId])
  const effectiveZoomBounds = useMemo(
    () => zoomBoundsFeet ?? getCombinedBounds(featureRectangles),
    [featureRectangles, zoomBoundsFeet]
  )
  const zoomRect = useMemo(() => {
    const bounds = effectiveZoomBounds
    if (!component || !bounds) return undefined
    return getPaddedFeatureZoomRect(component.id, bounds)
  }, [component, effectiveZoomBounds])
  const handleHovered = useCallback(
    (tooltipFrozen: boolean, event: AssetVizHoverEvent | undefined) => {
      if (tooltipFrozen) return
      onHoveredCoordsChange(event?.coords)
    },
    [onHoveredCoordsChange]
  )

  if (!referenceData.assetModel || !component || !inspection || !cdf) {
    return <PreviewUnavailable>Preview unavailable</PreviewUnavailable>
  }

  return (
    <VizContainer>
      <StyledAssetVizWindow
        id={`stcr-workflow-${title}-${component.id}-${inspection.id}-${cdf.id}`}
        focusZoomRect={zoomRect}
        assetModel={referenceData.assetModel}
        components={[component]}
        componentDataFrames={[cdf]}
        selectedCDF={cdf}
        resolutionDataFrames={resolutionCdfs}
        onSelectedCDFChange={onSelectResolution}
        selectedColorThreshold={selectedColorThreshold}
        onSelectedColorThresholdChange={onSelectedColorThresholdChange}
        filteredValueRange={filteredValueRange}
        onFilteredValueRangeChange={onFilteredValueRangeChange}
        showColorScaleSettings={showColorScaleSettings}
        selectedInspection={inspection}
        cameraFitMultiplier={0.75}
        initialShow3D={false}
        expandable={false}
        sceneConfig={{
          showFeatures: true,
          showFeatureLabels: true,
          showRulers: true,
          showCompass: false,
          showLegend: true,
        }}
        mode={AssetVizMode.SelectBin}
        markupLayers={markupLayers}
        onHovered={handleHovered}
        topLeftContent={<TitleOverlay>{title}</TitleOverlay>}
      />
    </VizContainer>
  )
}
